import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownItem from 'react-bootstrap/DropdownItem'
import { DropdownToggleBtn, StyledDropdownMenu } from './dropdown'
import ProfileAvatar from './profileAvatar'
import { userContext } from '../../contexts/userContext'
import { endSession } from '../../services/auth/session'
import { ReactComponent as Power } from '../../assets/svgs/power.svg'
import { ReactComponent as Person } from '../../assets/svgs/person.svg'
import { ReactComponent as Gear } from '../../assets/svgs/gear.svg'
import { useTheme } from '../../contexts/themeContext'
import apiAuth from '../../features/auth/services/auth'
import theme from '../../constants/theme'

const UserHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 16px 12px',
  backgroundColor: theme.mainContentBg,
})

const UserInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  lineHeight: 1.35,
})

const UserName = styled('span')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.textPrimary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const UserEmail = styled('span')(({ theme }) => ({
  fontSize: '12px',
  color: theme.textSecondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const RoleBadge = styled('span')(({ theme }) => ({
  marginTop: '5px',
  alignSelf: 'flex-start',
  fontSize: '11px',
  fontWeight: 600,
  color: theme.brandColorOrange,
  backgroundColor: `${theme.brandColorOrange}1A`,
  padding: '2px 8px',
  borderRadius: '6px',
  textTransform: 'capitalize',
}))

const Divider = styled('div')(({ theme }) => ({
  height: '1px',
  margin: '4px 8px',
  backgroundColor: theme.footerBg,
  border: 'none',
}))

const MenuItem = styled(DropdownItem)(({ theme, danger }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  color: danger ? theme.dangerColor : theme.textPrimary,
  '& svg': {
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  '& svg path': {
    fill: 'currentColor',
  },
  '&:hover, &:focus, &:active': {
    backgroundColor: theme.gray50,
    color: danger ? theme.dangerColor : theme.textPrimary,
  },
}))

export default function ProfileMenu() {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = React.useContext(userContext)
  const [menu, setMenu] = React.useState(false)

  const handleLogout = () => {
    // Session/company state is cleared by endSession(); customColor is a display
    // preference, so it is dropped here rather than treated as session state.
    localStorage.removeItem('customColor')
    endSession().finally(() => {
      window.location.href = '/login'
    })
  }
  const goToUserProfile = () => navigate('/profile?tab=user')
  const goToAccountSettings = () => navigate('/display-settings')

  const userRole = (user.state?.roleType || user.state?.userTitle || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <Dropdown
      show={menu}
      onToggle={() => setMenu(!menu)}
      className="d-inline-block"
      align="end"
    >
      <DropdownToggleBtn
        className="btn header-item "
        id="page-header-user-dropdown"
        tag="button"
        color={theme.headerSafeTextColor}
      >
        <ProfileAvatar
          size={3}
          name={user.state?.fullname}
          picture={user.state?.picture}
          fontSize="12px"
        />
      </DropdownToggleBtn>
      <StyledDropdownMenu className="h-auto">
        <UserHeader>
          <ProfileAvatar
            size={2.6}
            name={user.state?.fullname}
            picture={user.state?.picture}
            fontSize="14px"
          />
          <UserInfo>
            <UserName>{user.state?.fullname}</UserName>
            <UserEmail>{user.state?.email}</UserEmail>
            {userRole && <RoleBadge>{userRole}</RoleBadge>}
          </UserInfo>
        </UserHeader>

        {user?.state?.isActive && (
          <MenuItem as="button" onClick={goToUserProfile}>
            <Person />
            {t('layout.profile')}
          </MenuItem>
        )}
        <MenuItem as="button" onClick={goToAccountSettings}>
          <Gear />
          {t('displaySettings.displaySettings')}
        </MenuItem>

        <Divider />

        <MenuItem as="button" danger onClick={handleLogout}>
          <Power />
          <span>{t('layout.logout')}</span>
        </MenuItem>
      </StyledDropdownMenu>
    </Dropdown>
  )
}
